import type React from 'react';
import { BranchSelector } from '@/components/layout/BranchSelector';
import { useEffect, useState } from 'react';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
import { recallFilters } from '@/lib/filterMemory';
import {
  Radar,
  Activity,
  Building2,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  FileText,
  Eye,
  Gauge,
  Layers,
  LineChart,
  LogOut,
  Map,
  PanelLeft,
  ScanSearch,
  SlidersHorizontal,
  Plug,
  Send,
  ShieldCheck,
  Server,
  Terminal,
  Users,
  Users2,
  Wand2,
  Wrench,
  Database,
  EyeOff,
  CircleDollarSign,
} from 'lucide-react';
import {cn} from '@/lib/utils';
import {setFinanceHidden, useFinanceHidden} from '@/lib/finance';
import {Button} from '@/components/ui/button';
import {useT} from '@/contexts/I18nContext';
import {computeInitials, getCurrentUsername} from '@/lib/auth';
import {useCurrentUser} from '@/hooks/useCurrentUser';
import {useCanAnyZone} from '@/hooks/useCan';
import {useCurrentZone} from '@/hooks/useCurrentZone';
import {useSidebarFlash, useZoneFeatures} from '@/hooks/useZoneFeatures';
import {FUNCTION_SETTINGS_PATHS, useFunctionSettingsItems} from '@/components/layout/functionSettingsNav';
import { Tooltip } from '@/components/ui/tooltip';

interface NavEntry {
  to: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Пункт активен и на этих страницах («Настройки функций» — на настройках любой функции). */
  activePaths?: string[];
}

interface NavGroup {
  titleKey: string;
  items: NavEntry[];
}

// Stage I: restore the production sidebar shape (Live / Аналитика / Настройка /
// Система) and quarantine everything half-built behind a superuser-only
// "Экспериментальные" group at the very bottom. One-branch flow — «Все филиалы»
// is no longer a Live entry; it lives in Экспериментальные.

// Live — operative items used during a shift
const LIVE_DASHBOARD: NavEntry = { to: '/usage/dashboard', labelKey: 'sidebar.dashboard', icon: Activity };
const LIVE_CHECKLISTS: NavEntry = { to: '/usage/tasks', labelKey: 'sidebar.checklistsLive', icon: ClipboardList };
const LIVE_VISITS: NavEntry = { to: '/usage/visits', labelKey: 'sidebar.visitsLive', icon: Eye };

// Аналитика — read-only insights for managers and above. Итоги дня + Итоги
// недели are now folded into the single "Отчёт" entry (День/Неделя picker).
const ANALYTICS_ITEMS: NavEntry[] = [
  // «Статус» — rolling-сводка отклонений, свой период у каждого типа данных (Алексей 24.09, AI185).
  // На проде открыт Алексеем 21.10 (#7014), до этого с 08.10 был только на стейджинге.
  // Первым в «Аналитике» (Алексей 23.10, #2065).
  { to: '/management/status', labelKey: 'sidebar.status', icon: Radar },
  { to: '/usage/report', labelKey: 'sidebar.report', icon: FileText },
  // Тепловая карта lives inside Статистика as a tab now.
  { to: '/management/analytics', labelKey: 'sidebar.statistics', icon: LineChart },
];

// Superuser-only — system-wide settings (users, etc.)
const SYSTEM_ITEMS: NavEntry[] = [
  { to: '/admin/branches', labelKey: 'sidebar.systemBranches', icon: Building2 },
  { to: '/admin/users', labelKey: 'sidebar.systemUsers', icon: ShieldCheck },
  { to: '/admin/groups', labelKey: 'sidebar.systemGroups', icon: Users2 },
  { to: '/admin/local-computers', labelKey: 'sidebar.systemLocalComputers', icon: Server },
  { to: '/admin/host-infra', labelKey: 'sidebar.systemHostInfra', icon: Terminal },
];

// Служебные — staff-only (is_staff, not just superuser). Report validation +
// the data annotation tool. Sits below Система.
const SERVICE_ITEMS: NavEntry[] = [
  { to: '/service/report-validation', labelKey: 'sidebar.reportValidation', icon: ClipboardCheck },
  // Лента предсказаний моделей по филиалу (Алексей 23.10 #2137).
  { to: '/service/predictions', labelKey: 'sidebar.predictionsFeed', icon: ScanSearch },
  // #159 Калибровка полигонов — moved here from Настройка (Alexey 2026-08-25):
  // point-acceptance tool for our own staff, same audience as report validation,
  // so it lives next to it in Служебные and is staff-only (StaffGuard).
  { to: '/management/calibration', labelKey: 'sidebar.calibration', icon: SlidersHorizontal },
  { to: '/admin/data', labelKey: 'sidebar.systemData', icon: Database },
];

// Настройки (Алексей 04.11 #4031): «Филиал» — сам филиал, «Настройки функций» — все включённые
// функции вкладками (порядок как в «Данных», functionSettingsNav.ts), дальше оборудование и пр.
const BRANCH_GENERAL: NavEntry = { to: '/management/general', labelKey: 'sidebar.general', icon: Building2 };
const BRANCH_REST: NavEntry[] = [
  { to: '/management/equipment', labelKey: 'sidebar.equipment', icon: Wrench },
  // «Интеграции» — подключение ОФД (Алексей 04.11 #4036).
  { to: '/management/receipts', labelKey: 'sidebar.branchIntegrations', icon: Plug },
  { to: '/management/telegram', labelKey: 'sidebar.notifications', icon: Send },
  { to: '/management/access', labelKey: 'sidebar.access', icon: Users },
];

// Экспериментальные — superuser-only, below everything. Half-built / paused
// surfaces parked here while we iterate without disrupting users.
const EXPERIMENTAL_ITEMS: NavEntry[] = [
  { to: '/usage/portfolio', labelKey: 'sidebar.portfolio', icon: Layers },
  { to: '/management/setup-wizard', labelKey: 'sidebar.setupWizard', icon: Wand2 },
  { to: '/management/plan', labelKey: 'sidebar.plan', icon: Map },
  { to: '/management/integrations', labelKey: 'sidebar.integrations', icon: Gauge },
];

interface SidebarProps {
  /** Desktop collapse state (icons only). */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Mobile drawer needs the close affordance after navigation. */
  onNavigate?: () => void;
  /** Logout handler so the footer button has somewhere to go. */
  onLogout: () => void;
  /** When true, render compactly for mobile drawer (no collapse button). */
  variant?: 'desktop' | 'mobile';
}

export function Sidebar({ collapsed, onToggleCollapsed, onNavigate, onLogout, variant = 'desktop' }: SidebarProps) {
  const isMobile = variant === 'mobile';
  // On mobile drawer the sidebar is always full-width (collapsed has no meaning).
  const isCompact = !isMobile && collapsed;
  const location = useLocation();
  // Only the global ?zone=… follows the user across nav clicks. Per-tab
  // state (mode for /usage/heatmap, camera_id for зонирование) belongs
  // to the originating tab and should NOT bleed into the URL of an
  // unrelated page — otherwise the wrong default state gets restored
  // next time someone lands on that page with a stale URL.
  const search = (() => {
    const inbound = new URLSearchParams(location.search);
    const out = new URLSearchParams();
    const zone = inbound.get('zone');
    if (zone) out.set('zone', zone);
    return out.toString() ? `?${out.toString()}` : '';
  })();
  // …а «константные» фильтры самой страницы (период, вкладка, дата) — из её
  // памяти (п. 74): ссылка в разметке остаётся голой (hover/isActive), а
  // переход подставляет их В МОМЕНТ клика — память пишется страницей в
  // эффекте, и search, посчитанный при рендере, отставал бы на шаг.
  const navigate = useNavigate();
  const searchFor = (pathname: string) => {
    const out = recallFilters(pathname);
    const zone = new URLSearchParams(location.search).get('zone');
    if (zone) out.set('zone', zone); else out.delete('zone');
    const s = out.toString();
    return s ? `?${s}` : '';
  };
  const go = (e: React.MouseEvent<HTMLAnchorElement>, pathname: string) => {
    onNavigate?.();
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate({ pathname, search: searchFor(pathname) });
  };
  const { t } = useT();

  // Аналитика group visible for managers and above (not line_worker).
  const canViewReports = useCanAnyZone('reports.menu');
  // Settings group visible only if user can edit config in at least one zone.
  const canManage = useCanAnyZone('zone.config.edit');
  // System + Экспериментальные groups visible only to platform superusers.
  const { user } = useCurrentUser();
  const isSuperuser = user?.is_superuser === true;
  // Служебные group visible to staff (a superset of superusers).
  const isStaff = user?.is_staff === true || isSuperuser;

  // Every «is this function on» answer comes from ONE registry (M1):
  // enabled → its settings tab is pinned under «Настройка функций»;
  // enabled + has_content → it also shows up in Live (and Статистика/Отчёт).
  const { zoneId } = useCurrentZone();
  const { features } = useZoneFeatures(zoneId);
  const flashPath = useSidebarFlash();
  const functionItems = useFunctionSettingsItems(zoneId);

  const tasksConfigured = !!(features?.checklists?.enabled && features.checklists.has_content);
  const visitsConfigured = !!(features?.visits?.enabled && features.visits.has_content);
  const liveItems: NavEntry[] = [
    LIVE_DASHBOARD,
    ...(tasksConfigured ? [LIVE_CHECKLISTS] : []),
    ...(visitsConfigured ? [LIVE_VISITS] : []),
  ];
  // LIVE — всегда группой с заголовком, даже из одного «Дашборда смены» (Алексей 23.10, #2065):
  // раньше без чек-листов/визитов она сворачивалась в крупную кнопку, и меню скакало при
  // переключении филиалов.

  // «Настройки функций» ведёт на первую включённую функцию; вкладки остальных — на самой странице.
  const settingsItems: NavEntry[] = [
    BRANCH_GENERAL,
    ...(functionItems.length ? [{
      to: functionItems[0].to, labelKey: 'sidebar.functionSettings', icon: SlidersHorizontal,
      activePaths: FUNCTION_SETTINGS_PATHS,
    }] : []),
    ...BRANCH_REST,
  ];
  const settingsGroups: NavGroup[] = [{ titleKey: 'sidebar.group.settings', items: settingsItems }];

  // То, что видит клиент.
  // По умолчанию развёрнуто: спрятать служебное — осознанный жест, а не то, что
  // человек должен каждый раз искать.
  const [internalOpen, setInternalOpen] = useState(() => {
    try { return localStorage.getItem('sidebar.internalOpen') !== '0'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('sidebar.internalOpen', internalOpen ? '1' : '0'); } catch { /* приватное окно — переживём */ }
  }, [internalOpen]);

  const NAV: NavGroup[] = [
    { titleKey: 'sidebar.group.live', items: liveItems },
    ...(canViewReports ? [{ titleKey: 'sidebar.group.analytics', items: ANALYTICS_ITEMS }] : []),
    ...(canManage ? settingsGroups : []),
  ];
  // Наше служебное. Живёт под разделителем, а не вперемешку с клиентским
  // (Алексей 16.09): разделитель, а не спойлер — в развёрнутом виде это тонкая
  // черта, которая просто говорит, где кончается клиентская часть.
  const INTERNAL_NAV: NavGroup[] = [
    ...(isSuperuser ? [{ titleKey: 'sidebar.group.system', items: SYSTEM_ITEMS }] : []),
    ...(isStaff ? [{ titleKey: 'sidebar.group.service', items: SERVICE_ITEMS }] : []),
    ...(isSuperuser ? [{ titleKey: 'sidebar.group.experimental', items: EXPERIMENTAL_ITEMS }] : []),
  ];

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
        isMobile ? 'w-full' : 'transition-[width] duration-200',
        !isMobile && (isCompact ? 'w-[64px]' : 'w-[240px]')
      )}
    >
      {/* Header — Linza logo + lens glyph + collapse toggle. */}
      <div
        className={cn(
          'flex shrink-0 items-center border-b border-sidebar-border',
          isMobile ? 'py-2.5' : 'h-14',
          isCompact ? 'justify-center px-2' : 'gap-2 px-3'
        )}
      >
        {isMobile && (
          // На телефоне вместо логотипа — выбор филиала крупным блоком (AI12.6).
          <BranchSelector variant="block" onPicked={onNavigate} />
        )}
        {!isCompact && !isMobile && (
          <img
            src="https://storage.yandexcloud.net/linza-media/static/img/logo_linza.png"
            alt="Linza Metrics"
            className="h-7 w-auto max-w-[140px] shrink-0 object-contain object-left"
          />
        )}
        {!isCompact && <FinanceToggle className={isMobile ? 'ml-1' : 'ml-auto'} />}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onToggleCollapsed}
            className={cn(
              'text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground shrink-0',
            )}
            title={isCompact ? t('common.expandMenu') : t('common.collapseMenu')}
            aria-label={isCompact ? t('common.expandMenu') : t('common.collapseMenu')}
          >
            <PanelLeft className="size-4" />
          </Button>
        )}
      </div>

      {/* Nav groups */}
      <nav className="no-scrollbar flex-1 overflow-y-auto py-3">
        {NAV.map((group, idx) => {
          const isFirst = idx === 0;
          // Mobile + collapsed-desktop hide group titles, so a divider keeps
          // the sections visually distinct. On expanded desktop the title
          // already separates groups — keep the spacing-only style there.
          const dividerClass = !isFirst && (isMobile || isCompact)
            ? 'mt-3 border-t border-sidebar-border pt-3'
            : '';
          return (
            <div key={group.titleKey} className={cn('mb-4 last:mb-0', dividerClass)}>
              {!isCompact && (
                <div className="mb-1.5 px-4 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted-foreground">
                  {t(group.titleKey)}
                </div>
              )}
              <ul className="space-y-0.5 px-2">
                {group.items.map(item => {
                  const Icon = item.icon;
                  const label = t(item.labelKey);
                  const alsoActive = !!item.activePaths?.some(p => location.pathname.startsWith(p));
                  return (
                    <li key={`${item.labelKey}:${item.to}`}>
                      <NavLink
                        to={{ pathname: item.to, search }}
                        onClick={(e) => go(e, item.to)}
                        className={({ isActive }) =>
                          cn(
                            'group flex items-center gap-2.5 rounded-lg border-l-2 px-3 py-2 text-[13px] font-medium transition-colors',
                            isCompact && 'justify-center px-0',
                            // M1.6: the tab that just appeared after «Включить» blinks.
                            (flashPath === item.to || (!!flashPath && !!item.activePaths?.includes(flashPath))) && 'animate-pulse ring-2 ring-primary/60',
                            isActive || alsoActive
                              ? 'border-l-primary bg-sidebar-active text-sidebar-active-foreground'
                              : 'border-l-transparent text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground'
                          )
                        }
                        title={isCompact ? label : undefined}
                      >
                        <Icon className="size-4 shrink-0" />
                        {!isCompact && <span className="truncate">{label}</span>}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}

        {INTERNAL_NAV.length > 0 && (
          <>
            {/* Разделитель, а не спойлер: тонкая черта с подписью, по клику
                сворачивает служебное. Показывает, где кончается клиентская часть. */}
            <Tooltip content={internalOpen ? 'Свернуть служебное' : 'Показать служебное'}><button
              type="button"
              onClick={() => setInternalOpen((v) => !v)}
              aria-label={internalOpen ? 'Свернуть служебное' : 'Показать служебное'}
              aria-expanded={internalOpen}
              // Без подписи вообще (Алексей 16.09): слово «Служебное» спорило с
              // заголовками групп и читалось как ещё один раздел. Разделитель должен
              // быть чертой, а не строкой текста — стрелка появляется по наведению.
              className={cn(
                'group my-2 flex w-full items-center gap-1.5 px-3 text-sidebar-muted-foreground transition-colors hover:text-sidebar-foreground',
                isCompact && 'px-1',
              )}
            >
              <span className="h-px flex-1 bg-sidebar-border" />
              <ChevronDown
                className={cn(
                  'size-3 shrink-0 opacity-0 transition-all group-hover:opacity-100',
                  !internalOpen && '-rotate-90 opacity-60',
                )}
              />
              <span className="h-px flex-1 bg-sidebar-border" />
            </button></Tooltip>

            {internalOpen && INTERNAL_NAV.map((group) => (
              <div key={group.titleKey} className="mb-4 last:mb-0">
                {!isCompact && (
                  <div className="mb-1.5 px-4 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted-foreground">
                    {t(group.titleKey)}
                  </div>
                )}
                <ul className="space-y-0.5 px-2">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const label = t(item.labelKey);
                    return (
                      <li key={item.to}>
                        <NavLink
                          to={{ pathname: item.to, search }}
                          onClick={(e) => go(e, item.to)}
                          className={({ isActive }) =>
                            cn(
                              'group flex items-center gap-2.5 rounded-lg border-l-2 px-3 py-2 text-[13px] font-medium transition-colors',
                              isCompact && 'justify-center px-0',
                              isActive
                                ? 'border-l-primary bg-sidebar-active text-sidebar-active-foreground'
                                : 'border-l-transparent text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground',
                            )
                          }
                          title={isCompact ? label : undefined}
                        >
                          <Icon className="size-4 shrink-0" />
                          {!isCompact && <span className="truncate">{label}</span>}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </>
        )}
      </nav>

      {/* Footer — current user + logout (signal-tracker style) */}
      <div className="border-t border-sidebar-border p-2">
        <UserFooter
          isCompact={isCompact}
          onLogout={onLogout}
          onNavigate={onNavigate}
          search={search}
          signOutLabel={t('common.signOut')}
          settingsLabel={t('account.title')}
        />
      </div>
    </aside>
  );
}

interface UserFooterProps {
  isCompact: boolean;
  onLogout: () => void;
  onNavigate?: () => void;
  search: string;
  signOutLabel: string;
  settingsLabel: string;
}

/** «Скрыть финансовые данные» (Алексей 27.10 #2956): выручка и чеки в рублях → «••• ₽», на этом устройстве. */
function FinanceToggle({ className }: { className?: string }) {
  const hidden = useFinanceHidden();
  const label = hidden ? 'Показать финансовые данные' : 'Скрыть финансовые данные';
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={() => setFinanceHidden(!hidden)}
      className={cn(
        'shrink-0 hover:bg-sidebar-muted hover:text-sidebar-foreground',
        hidden ? 'text-sidebar-foreground' : 'text-sidebar-muted-foreground',
        className
      )}
      title={label}
      aria-label={label}
      aria-pressed={hidden}
    >
      {hidden ? <EyeOff className="size-4" /> : <CircleDollarSign className="size-4" />}
    </Button>
  );
}

function UserFooter({ isCompact, onLogout, onNavigate, search, signOutLabel, settingsLabel }: UserFooterProps) {
  // Prefer the freshly-fetched user from /users/me/, fall back to whatever
  // LoginPage stashed in localStorage (and from there to JWT). This keeps
  // tg-auto-login users named once /users/me/ resolves.
  const { user } = useCurrentUser();
  const username = user?.username ?? getCurrentUsername();
  const initials = computeInitials(username);

  if (isCompact) {
    return (
      <div className="flex flex-col items-center gap-1.5">
        <NavLink
          to={{ pathname: '/settings/account', search }}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex size-8 items-center justify-center rounded-full bg-sidebar-active text-[11px] font-semibold uppercase text-sidebar-active-foreground transition-shadow hover:ring-2 hover:ring-white/30',
              isActive && 'ring-2 ring-white/60',
            )
          }
          title={username ? `${username} · ${settingsLabel}` : settingsLabel}
          aria-label={settingsLabel}
        >
          {initials}
        </NavLink>
        <Tooltip content={signOutLabel}><button
          type="button"
          onClick={onLogout}
          className="flex size-8 items-center justify-center rounded-md text-sidebar-muted-foreground transition-colors hover:bg-sidebar-muted hover:text-destructive"
          aria-label={signOutLabel}
        >
          <LogOut className="size-4" />
        </button></Tooltip>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <NavLink
        to={{ pathname: '/settings/account', search }}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'group flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors',
            isActive ? 'bg-sidebar-active text-sidebar-active-foreground' : 'hover:bg-sidebar-muted',
          )
        }
        title={settingsLabel}
      >
        <div
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sidebar-active text-[11px] font-semibold uppercase text-sidebar-active-foreground"
          aria-hidden="true"
        >
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-sidebar-foreground">
            {username ?? '—'}
          </div>
          <div className="truncate text-[11px] text-sidebar-muted-foreground">
            {settingsLabel}
          </div>
        </div>
      </NavLink>
      <Tooltip content={signOutLabel}><button
        type="button"
        onClick={onLogout}
        className="flex size-7 shrink-0 items-center justify-center rounded-md text-sidebar-muted-foreground transition-colors hover:bg-sidebar-muted hover:text-destructive"
        aria-label={signOutLabel}
      >
        <LogOut className="size-4" />
      </button></Tooltip>
    </div>
  );
}

